import {
  AppPlatform,
  joinAppWaitlist,
} from "@/api/privateRequests/user/appWaitlist";
import { Button } from "@/design-system/Button/Button";
import { CheckBox } from "@/design-system/Check/Check";
import Input from "@/design-system/Input/Input";
import { SegmentedControl } from "@/design-system/SegmentedControl/SegmentedControl";
import { cn } from "@/lib/utils";
import { AppDispatch } from "@/redux/store";
import { getUserSelector, updateUser } from "@/redux/user/userSlice";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { isAxiosError } from "axios";
import { CircleCheck } from "lucide-react";
import { FormEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router";

import { AndroidIcon, AppleIcon } from "./PlatformIcons";
import { detectPlatform } from "./detectPlatform";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type State = "idle" | "joining" | "joined" | "error" | "throttled";

type Props = {
  /** "nav" is the menu card: no platform heading, tighter spacing. */
  form?: "landing" | "nav";
  className?: string;
};

export const AppWaitlistForm = ({ form = "landing", className }: Props) => {
  const compact = form === "nav";
  const { t } = useTranslation("appLanding");
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);
  const loggedIn = !!user.uuid;

  const [platform, setPlatform] = useState(detectPlatform);
  const [needsPlatform, setNeedsPlatform] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>("idle");
  const [invalid, setInvalid] = useState(false);
  // Explicit opt-in: starts unchecked; submitting without it nudges instead.
  const [consented, setConsented] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (state === "joining") return;
    const emailInvalid = !loggedIn && !EMAIL_PATTERN.test(email.trim());
    setInvalid(emailInvalid);
    setNeedsConsent(!consented);
    setNeedsPlatform(!platform);
    if (!platform || emailInvalid || !consented) return;

    setState("joining");
    try {
      // Logged in, the backend ties the signup to the account's own email.
      await joinAppWaitlist(platform, loggedIn ? user.email : email.trim());
      if (loggedIn && !user.emailNotification) {
        dispatch(updateUser({ emailNotification: true }));
      }
      setState("joined");
    } catch (err) {
      // The backend allows a few signups per IP an hour; retrying won't help.
      setState(
        isAxiosError(err) && err.response?.status === 429
          ? "throttled"
          : "error",
      );
    }
  };

  if (state === "joined") {
    return (
      <div
        role="status"
        className={cn("flex flex-col", compact ? "gap-2" : "gap-3", className)}
      >
        <div className="flex items-center gap-2.5">
          <CircleCheck aria-hidden className="size-6 text-complete" />
          <p className="text-lg font-semibold text-neutral-50">
            {t("form.successTitle")}
          </p>
        </div>
        <p className="text-[15px] text-neutral-400">
          {t(`form.success.${platform}`)}
        </p>
        <Button
          variant="secondary"
          size="small"
          onClick={() => {
            setPlatform(platform === "ios" ? "android" : "ios");
            setState("idle");
          }}
        >
          {t("form.another")}
        </Button>
      </div>
    );
  }

  const message = needsPlatform
    ? t("form.platformRequired")
    : invalid
      ? t("form.invalidEmail")
      : needsConsent
        ? t("form.consentRequired")
        : state === "error"
          ? t("form.error")
          : state === "throttled"
            ? t("form.throttled")
            : null;

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      className={cn("flex flex-col", compact ? "gap-3" : "gap-5", className)}
    >
      <div className="flex flex-col gap-2.5">
        {!compact && (
          <span className="text-sm font-semibold text-neutral-100">
            {t("form.platformLabel")}
          </span>
        )}
        <SegmentedControl
          label={t("form.platformLabel")}
          variant="primary"
          track="page"
          className="w-fit"
          value={platform}
          onChange={(value) => {
            setPlatform(value);
            setNeedsPlatform(false);
          }}
          options={[
            {
              value: "ios",
              label: (
                <>
                  <AppleIcon className="size-4" />
                  {t("form.platform.ios")}
                </>
              ),
            },
            {
              value: "android",
              label: (
                <>
                  <AndroidIcon className="size-4" />
                  {t("form.platform.android")}
                </>
              ),
            },
          ]}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2.5">
          {!loggedIn && (
            <div className="min-w-[220px] flex-1">
              <Input
                type="email"
                autoComplete="email"
                aria-label={t("form.emailLabel")}
                aria-invalid={invalid}
                placeholder={t("form.placeholder")}
                value={email}
                onChange={(value) => {
                  setEmail(value);
                  if (invalid) setInvalid(false);
                }}
                className={cn(invalid && "border-error")}
              />
            </div>
          )}
          <Button
            type="submit"
            variant="primary"
            size="medium"
            disabled={state === "joining"}
            className="shrink-0 whitespace-nowrap"
          >
            {t("form.submit")}
          </Button>
        </div>
        <label className="flex cursor-pointer select-none items-start gap-2 text-left text-xs text-neutral-400">
          <input
            type="checkbox"
            checked={consented}
            onChange={() => {
              setConsented((c) => !c);
              setNeedsConsent(false);
            }}
            className="peer sr-only"
          />
          <CheckBox
            checked={consented}
            className={cn(
              "mt-px peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-blue",
              !consented &&
                (needsConsent ? "border-error" : "border-neutral-500"),
            )}
          />
          <span>
            <Trans
              t={t}
              i18nKey="form.consent"
              // New tab, or reading the policy throws away the typed email
              components={[
                <Link
                  to="/legal/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-neutral-600 underline-offset-2 hover:text-neutral-200"
                />,
              ]}
            />
          </span>
        </label>
        {message && (
          <p role="alert" className="text-sm text-error">
            {message}
          </p>
        )}
      </div>
    </form>
  );
};
